import React, { useState, useEffect } from 'react';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Select } from '../../ui/Select';
import { Badge } from '../../ui/Badge';
import { colors, agentThemes, spacing, borderRadius, shadows } from '../../../constants/theme';
import { Users, TrendingUp, Target, Clock, DollarSign, ShoppingCart, Heart, AlertTriangle, ArrowUp } from 'lucide-react';

interface TargetSegment {
  id: string;
  name: string;
  description: string;
  size: number;
  characteristics: Characteristic[];
  behavior: BehaviorPattern;
  performance: SegmentPerformance;
  recommendations: Recommendation[];
  score: number;
}

interface Characteristic {
  type: 'demographic' | 'psychographic' | 'behavioral' | 'geographic';
  attribute: string;
  value: string | number;
  confidence: number;
}

interface BehaviorPattern {
  purchaseFrequency: number;
  averageOrderValue: number;
  preferredCategories: string[];
  preferredChannels: string[];
  purchaseTime: { day: string; hour: number }[];
  seasonality: { month: string; index: number }[];
}

interface SegmentPerformance {
  revenue: number;
  conversionRate: number;
  retentionRate: number;
  ltv: number;
  churnRate: number;
  engagementScore: number;
}

interface Recommendation {
  id: string;
  type: 'product' | 'channel' | 'timing' | 'message' | 'offer';
  title: string;
  description: string;
  expectedImpact: number;
  confidence: number;
}

interface OptimizationStrategy {
  segmentId: string;
  tactics: Tactic[];
  expectedROI: number;
  implementationCost: number;
  timeline: string;
}

interface Tactic {
  id: string;
  name: string;
  channel: string;
  message: string;
  timing: string;
  budget: number;
}

interface TargetingOptimizerProps {
  onSegmentSelect?: (segment: TargetSegment) => void;
  onStrategyApply?: (strategy: OptimizationStrategy) => void;
}

const TargetingOptimizer: React.FC<TargetingOptimizerProps> = ({
  onSegmentSelect,
  onStrategyApply
}) => {
  const [segments, setSegments] = useState<TargetSegment[]>([]);
  const [selectedSegment, setSelectedSegment] = useState<TargetSegment | null>(null);
  const [viewMode, setViewMode] = useState<'segments' | 'analysis' | 'strategy'>('segments');
  const [loading, setLoading] = useState(false);

  // サンプルセグメントデータ生成
  const generateSegments = (): TargetSegment[] => {
    return [
      {
        id: 'seg001',
        name: 'プレミアム顧客',
        description: '高頻度・高単価の優良顧客層',
        size: 2500,
        score: 95,
        characteristics: [
          { type: 'demographic', attribute: '年齢', value: '35-50歳', confidence: 85 },
          { type: 'demographic', attribute: '世帯年収', value: '800万円以上', confidence: 75 },
          { type: 'behavioral', attribute: '購買頻度', value: '週2回以上', confidence: 90 },
          { type: 'psychographic', attribute: '価値観', value: '品質重視', confidence: 80 }
        ],
        behavior: {
          purchaseFrequency: 8.5,
          averageOrderValue: 12000,
          preferredCategories: ['プレミアム食品', 'オーガニック', 'ワイン'],
          preferredChannels: ['アプリ', '店舗'],
          purchaseTime: [{ day: '土曜', hour: 14 }, { day: '日曜', hour: 10 }],
          seasonality: [{ month: '12月', index: 150 }, { month: '8月', index: 120 }]
        },
        performance: {
          revenue: 25000000,
          conversionRate: 18.5,
          retentionRate: 92,
          ltv: 480000,
          churnRate: 2.3,
          engagementScore: 88
        },
        recommendations: [
          {
            id: 'rec001',
            type: 'product',
            title: 'プレミアムライン拡充',
            description: '高級食材・オーガニック商品の品揃え強化',
            expectedImpact: 15,
            confidence: 85
          },
          {
            id: 'rec002',
            type: 'offer',
            title: 'VIP会員特別優待',
            description: '年会費制の特別会員プログラムを導入',
            expectedImpact: 12,
            confidence: 78
          }
        ]
      },
      {
        id: 'seg002',
        name: 'ファミリー層',
        description: '子育て世帯・まとめ買い中心',
        size: 8500,
        score: 82,
        characteristics: [
          { type: 'demographic', attribute: '年齢', value: '30-45歳', confidence: 82 },
          { type: 'demographic', attribute: '家族構成', value: '3-5人', confidence: 88 },
          { type: 'behavioral', attribute: '購買頻度', value: '週1-2回', confidence: 85 },
          { type: 'psychographic', attribute: '価値観', value: 'コスパ重視', confidence: 75 }
        ],
        behavior: {
          purchaseFrequency: 5.2,
          averageOrderValue: 8500,
          preferredCategories: ['日配品', '冷凍食品', '洗剤・日用品'],
          preferredChannels: ['店舗', 'EC'],
          purchaseTime: [{ day: '土曜', hour: 16 }, { day: '日曜', hour: 11 }],
          seasonality: [{ month: '3月', index: 130 }, { month: '9月', index: 125 }]
        },
        performance: {
          revenue: 45000000,
          conversionRate: 12.3,
          retentionRate: 78,
          ltv: 320000,
          churnRate: 5.8,
          engagementScore: 72
        },
        recommendations: [
          {
            id: 'rec003',
            type: 'offer',
            title: 'まとめ買い割引強化',
            description: '3点以上購入で段階的割引を適用',
            expectedImpact: 18,
            confidence: 82
          },
          {
            id: 'rec004',
            type: 'channel',
            title: 'ネットスーパー利便性向上',
            description: '配送時間枠拡大と即日配送エリア拡大',
            expectedImpact: 22,
            confidence: 76
          }
        ]
      },
      {
        id: 'seg003',
        name: 'シニア層',
        description: '65歳以上・健康志向',
        size: 4200,
        score: 75,
        characteristics: [
          { type: 'demographic', attribute: '年齢', value: '65歳以上', confidence: 92 },
          { type: 'behavioral', attribute: '購買頻度', value: '週3-4回', confidence: 80 },
          { type: 'psychographic', attribute: '関心事', value: '健康・安全', confidence: 85 }
        ],
        behavior: {
          purchaseFrequency: 12.5,
          averageOrderValue: 3500,
          preferredCategories: ['生鮮食品', '健康食品', '医薬品'],
          preferredChannels: ['店舗'],
          purchaseTime: [{ day: '平日', hour: 10 }],
          seasonality: [{ month: '1月', index: 110 }]
        },
        performance: {
          revenue: 18000000,
          conversionRate: 15.2,
          retentionRate: 85,
          ltv: 280000,
          churnRate: 3.5,
          engagementScore: 68
        },
        recommendations: [
          {
            id: 'rec005',
            type: 'timing',
            title: 'シニアタイム導入',
            description: '平日午前中の特別割引時間帯設定',
            expectedImpact: 14,
            confidence: 80
          }
        ]
      }
    ];
  };

  useEffect(() => {
    const data = generateSegments();
    setSegments(data);
    if (data.length > 0) {
      setSelectedSegment(data[0]);
    }
  }, []);

  const handleSegmentSelect = (segment: TargetSegment) => {
    setSelectedSegment(segment);
    if (onSegmentSelect) {
      onSegmentSelect(segment);
    }
  };

  const generateStrategy = (segment: TargetSegment): OptimizationStrategy => {
    const tactics: Tactic[] = [];
    
    if (segment.id === 'seg001') {
      tactics.push(
        {
          id: 't001',
          name: 'VIPイベント開催',
          channel: '店舗',
          message: '特別試食会・新商品先行販売',
          timing: '月1回・週末',
          budget: 500000
        },
        {
          id: 't002',
          name: 'パーソナライズドメール',
          channel: 'メール',
          message: '購買履歴に基づく商品提案',
          timing: '週2回',
          budget: 100000
        }
      );
    } else if (segment.id === 'seg002') {
      tactics.push(
        {
          id: 't003',
          name: 'ファミリーセール',
          channel: '全チャネル',
          message: '家族向け大容量パック特価',
          timing: '月末週末',
          budget: 800000
        },
        {
          id: 't004',
          name: 'アプリクーポン配信',
          channel: 'アプリ',
          message: '子供向け商品割引クーポン',
          timing: '毎週金曜',
          budget: 200000
        }
      );
    }

    return {
      segmentId: segment.id,
      tactics,
      expectedROI: 320,
      implementationCost: tactics.reduce((sum, t) => sum + t.budget, 0),
      timeline: '3ヶ月'
    };
  };

  return (
    <div className="space-y-4">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold" style={{ color: colors.text.primary }}>
          ターゲティング最適化
        </h3>
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'segments' ? 'primary' : 'secondary'}
            size="small"
            onClick={() => setViewMode('segments')}
          >
            <Users size={14} />
            セグメント
          </Button>
          <Button
            variant={viewMode === 'analysis' ? 'primary' : 'secondary'}
            size="small"
            onClick={() => setViewMode('analysis')}
          >
            <TrendingUp size={14} />
            詳細分析
          </Button>
          <Button
            variant={viewMode === 'strategy' ? 'primary' : 'secondary'}
            size="small"
            onClick={() => setViewMode('strategy')}
          >
            <Target size={14} />
            施策戦略
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {/* 左側: セグメント一覧 */}
        <div>
          <Card className="p-3">
            <h4 className="text-sm font-semibold mb-3" style={{ color: colors.text.primary }}>
              顧客セグメント
            </h4>
            <div className="space-y-2">
              {segments.map(segment => (
                <div
                  key={segment.id}
                  onClick={() => handleSegmentSelect(segment)}
                  className={`p-2 rounded cursor-pointer transition-all ${
                    selectedSegment?.id === segment.id ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                  }`}
                  style={{
                    border: `1px solid ${
                      selectedSegment?.id === segment.id ? colors.accent.blue : colors.ui.border
                    }`
                  }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium" style={{ color: colors.text.primary }}>
                      {segment.name}
                    </span>
                    <Badge variant={segment.score >= 80 ? 'success' : 'warning'} size="sm">
                      {segment.score}
                    </Badge>
                  </div>
                  <p className="text-xs mb-2" style={{ color: colors.text.secondary }}>
                    {segment.description}
                  </p>
                  <div className="flex items-center justify-between text-xs">
                    <span style={{ color: colors.text.secondary }}>
                      {segment.size.toLocaleString()}人
                    </span>
                    <span style={{ color: colors.ui.success }}>
                      ¥{(segment.performance.revenue / 1000000).toFixed(1)}M
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* 中央: 詳細分析 */}
        <div>
          {selectedSegment && viewMode === 'analysis' && (
            <Card className="p-3">
              <h4 className="text-sm font-semibold mb-3" style={{ color: colors.text.primary }}>
                詳細分析: {selectedSegment.name}
              </h4>
              
              {/* KPI */}
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="p-2 bg-blue-50 rounded">
                  <div className="flex items-center justify-between mb-1">
                    <DollarSign size={14} className="text-blue-600" />
                    <span className="text-xs text-blue-600">+12%</span>
                  </div>
                  <div className="text-lg font-bold text-blue-700">
                    ¥{selectedSegment.performance.averageOrderValue || selectedSegment.behavior.averageOrderValue}
                  </div>
                  <div className="text-xs text-gray-600">平均購買額</div>
                </div>
                <div className="p-2 bg-green-50 rounded">
                  <div className="flex items-center justify-between mb-1">
                    <ShoppingCart size={14} className="text-green-600" />
                    <span className="text-xs text-green-600">+8%</span>
                  </div>
                  <div className="text-lg font-bold text-green-700">
                    {selectedSegment.behavior.purchaseFrequency.toFixed(1)}回/月
                  </div>
                  <div className="text-xs text-gray-600">購買頻度</div>
                </div>
                <div className="p-2 bg-purple-50 rounded">
                  <div className="flex items-center justify-between mb-1">
                    <Heart size={14} className="text-purple-600" />
                    <span className="text-xs text-purple-600">安定</span>
                  </div>
                  <div className="text-lg font-bold text-purple-700">
                    {selectedSegment.performance.retentionRate}%
                  </div>
                  <div className="text-xs text-gray-600">継続率</div>
                </div>
                <div className="p-2 bg-orange-50 rounded">
                  <div className="flex items-center justify-between mb-1">
                    <TrendingUp size={14} className="text-orange-600" />
                    <span className="text-xs text-orange-600">+15%</span>
                  </div>
                  <div className="text-lg font-bold text-orange-700">
                    ¥{(selectedSegment.performance.ltv / 1000).toFixed(0)}K
                  </div>
                  <div className="text-xs text-gray-600">LTV</div>
                </div>
              </div>

              {/* 行動パターン */}
              <div className="mb-3">
                <h5 className="text-xs font-semibold mb-2" style={{ color: colors.text.primary }}>
                  行動パターン
                </h5>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span style={{ color: colors.text.secondary }}>主要カテゴリ</span>
                    <div className="flex gap-1">
                      {selectedSegment.behavior.preferredCategories.slice(0, 2).map(cat => (
                        <Badge key={cat} variant="default" size="sm">{cat}</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span style={{ color: colors.text.secondary }}>利用チャネル</span>
                    <div className="flex gap-1">
                      {selectedSegment.behavior.preferredChannels.map(ch => (
                        <Badge key={ch} variant="secondary" size="sm">{ch}</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span style={{ color: colors.text.secondary }}>ピーク時間</span>
                    <span style={{ color: colors.text.primary }}>
                      {selectedSegment.behavior.purchaseTime[0]?.day} {selectedSegment.behavior.purchaseTime[0]?.hour}時
                    </span>
                  </div>
                </div>
              </div>

              {/* 特性 */}
              <div>
                <h5 className="text-xs font-semibold mb-2" style={{ color: colors.text.primary }}>
                  顧客特性
                </h5>
                <div className="space-y-1">
                  {selectedSegment.characteristics.map((char, idx) => (
                    <div key={idx} className="flex justify-between text-xs p-1 bg-gray-50 rounded">
                      <span style={{ color: colors.text.secondary }}>{char.attribute}</span>
                      <span style={{ color: colors.text.primary }}>{char.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          )}

          {selectedSegment && viewMode === 'segments' && (
            <Card className="p-3">
              <h4 className="text-sm font-semibold mb-3" style={{ color: colors.text.primary }}>
                セグメント概要
              </h4>
              <div className="space-y-3">
                <div>
                  <p className="text-sm mb-2" style={{ color: colors.text.primary }}>
                    {selectedSegment.description}
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span style={{ color: colors.text.secondary }}>セグメントサイズ</span>
                      <p className="font-bold" style={{ color: colors.text.primary }}>
                        {selectedSegment.size.toLocaleString()}人
                      </p>
                    </div>
                    <div>
                      <span style={{ color: colors.text.secondary }}>売上貢献</span>
                      <p className="font-bold" style={{ color: colors.accent.green }}>
                        ¥{(selectedSegment.performance.revenue / 1000000).toFixed(1)}M
                      </p>
                    </div>
                  </div>
                </div>
                <div>
                  <h5 className="text-xs font-semibold mb-2" style={{ color: colors.text.primary }}>
                    主要指標
                  </h5>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs" style={{ color: colors.text.secondary }}>CVR</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-gray-200 rounded-full">
                          <div 
                            className="h-2 bg-blue-500 rounded-full"
                            style={{ width: `${selectedSegment.performance.conversionRate * 2}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium">{selectedSegment.performance.conversionRate.toFixed(1)}%</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs" style={{ color: colors.text.secondary }}>継続率</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-gray-200 rounded-full">
                          <div 
                            className="h-2 bg-green-500 rounded-full"
                            style={{ width: `${selectedSegment.performance.retentionRate}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium">{selectedSegment.performance.retentionRate}%</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs" style={{ color: colors.text.secondary }}>エンゲージメント</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-gray-200 rounded-full">
                          <div 
                            className="h-2 bg-purple-500 rounded-full"
                            style={{ width: `${selectedSegment.performance.engagementScore}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium">{selectedSegment.performance.engagementScore}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* 右側: 施策戦略 */}
        <div>
          {selectedSegment && viewMode === 'strategy' && (
            <Card className="p-3">
              <h4 className="text-sm font-semibold mb-3" style={{ color: colors.text.primary }}>
                施策戦略
              </h4>
              <div className="space-y-3">
                {/* 推奨施策 */}
                <div>
                  <h5 className="text-xs font-semibold mb-2" style={{ color: colors.text.primary }}>
                    推奨施策
                  </h5>
                  <div className="space-y-2">
                    {selectedSegment.recommendations.map(rec => (
                      <div key={rec.id} className="p-2 border rounded" style={{ borderColor: colors.ui.border }}>
                        <div className="flex items-start justify-between mb-1">
                          <span className="text-xs font-medium" style={{ color: colors.text.primary }}>
                            {rec.title}
                          </span>
                          <Badge variant="success" size="sm">
                            +{rec.expectedImpact}%
                          </Badge>
                        </div>
                        <p className="text-xs mb-2" style={{ color: colors.text.secondary }}>
                          {rec.description}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs" style={{ color: colors.text.secondary }}>
                            信頼度: {rec.confidence}%
                          </span>
                          <Button variant="primary" size="small">
                            実行
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 実行計画 */}
                <div>
                  <h5 className="text-xs font-semibold mb-2" style={{ color: colors.text.primary }}>
                    実行計画
                  </h5>
                  {(() => {
                    const strategy = generateStrategy(selectedSegment);
                    return (
                      <div className="space-y-2">
                        {strategy.tactics.map(tactic => (
                          <div key={tactic.id} className="p-2 bg-gray-50 rounded">
                            <div className="flex justify-between items-start mb-1">
                              <span className="text-xs font-medium" style={{ color: colors.text.primary }}>
                                {tactic.name}
                              </span>
                              <span className="text-xs" style={{ color: colors.accent.green }}>
                                ¥{(tactic.budget / 1000).toFixed(0)}K
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-1 text-xs">
                              <div>
                                <span style={{ color: colors.text.secondary }}>チャネル: </span>
                                <span style={{ color: colors.text.primary }}>{tactic.channel}</span>
                              </div>
                              <div>
                                <span style={{ color: colors.text.secondary }}>実施: </span>
                                <span style={{ color: colors.text.primary }}>{tactic.timing}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                        <div className="p-2 bg-blue-50 rounded">
                          <div className="flex justify-between items-center">
                            <span className="text-xs font-medium text-blue-700">期待ROI</span>
                            <span className="text-lg font-bold text-blue-700">
                              {strategy.expectedROI}%
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </Card>
          )}

          {selectedSegment && viewMode !== 'strategy' && (
            <Card className="p-3">
              <h4 className="text-sm font-semibold mb-3" style={{ color: colors.text.primary }}>
                インサイト
              </h4>
              <div className="space-y-2">
                <div className="p-2 bg-blue-50 rounded">
                  <div className="flex items-start gap-2">
                    <TrendingUp size={14} className="text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-xs font-medium text-blue-700">成長機会</p>
                      <p className="text-xs text-gray-600">
                        {selectedSegment.name}は過去3ヶ月で
                        <span className="font-bold">+{Math.round(Math.random() * 20 + 5)}%</span>成長
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-2 bg-orange-50 rounded">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={14} className="text-orange-600 mt-0.5" />
                    <div>
                      <p className="text-xs font-medium text-orange-700">注意事項</p>
                      <p className="text-xs text-gray-600">
                        競合他社が同セグメントへの施策を強化中
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-2 bg-green-50 rounded">
                  <div className="flex items-start gap-2">
                    <Target size={14} className="text-green-600 mt-0.5" />
                    <div>
                      <p className="text-xs font-medium text-green-700">アクションポイント</p>
                      <p className="text-xs text-gray-600">
                        {selectedSegment.behavior.preferredChannels[0]}での接触を
                        <span className="font-bold">週{Math.round(selectedSegment.behavior.purchaseFrequency / 4)}回</span>に増加
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default TargetingOptimizer;